import { resolveExternalUrl } from './externalUrl.ts'
import {
  buildTradingAppDetailUrl,
  resolveTradingAppDefaultPlatform,
  IOS_PLATFORM,
  type TradingAppTraderAliasMap,
} from './homeTradingApps.ts'

/** 首页 Banner 的跳转类型：服务商详情页。 */
const BANNER_JUMP_TYPE_TRADER = 15

/** 首页 Banner 的跳转类型：排行榜。 */
const BANNER_JUMP_TYPE_RANKING = 16

/** 首页 Banner 的跳转类型：交易应用（软件详情页）。 */
const BANNER_JUMP_TYPE_SOFTWARE_APP = 18

/** 服务商详情页由 WikiBit 主站承接，路径不在本应用路由表内。 */
const WIKIBIT_MAIN_ORIGIN = 'https://www.wikibit.com'

/** 排行榜按设备区分应用下载榜，苹果设备走 iOS 榜，其余走 Android 榜。 */
const RANKING_SLUG_IOS = 'appdownload-ios'
const RANKING_SLUG_ANDROID = 'appdownload-android'

/** Banner 广告接口返回的原始字段。接口字段可能为空，展示层负责逐项回退。 */
export type StoreBannerItem = {
  id?: string | number | null
  bannerImage?: string | null
  marketId?: number | string | null
  webBannerPosition?: number | string | null
  appName?: string | null
  appTitle?: string | null
  traderName?: string | null
  appLogo?: string | null
  appCoverBackground?: string | null
  appBundleId?: string | null
  traderCode?: string | null
  softwareId?: string | null
  screenshots?: string[] | null
  downloadNum?: number | string | null
  sizes?: string | null
  traderIco?: string | null
  jumpType?: number | string | null
  jumpTypeName?: string | null
  code?: string | null
  jumpLink?: string | null
  language?: string | null
  country?: string | number | null
  ad?: string | null
}

type UnknownRecord = Record<string, unknown>

const isRecord = (value: unknown): value is UnknownRecord => {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

const hasOwn = (value: UnknownRecord, key: string) => Object.prototype.hasOwnProperty.call(value, key)

const isBusinessFailure = (value: UnknownRecord): boolean => {
  if (value.succeed === false || value.success === false) return true

  for (const key of ['code', 'httpCode', 'errorCode']) {
    const rawCode = value[key]
    if (rawCode === undefined || rawCode === null || rawCode === '') continue

    const code = Number(rawCode)
    if (Number.isFinite(code) && code !== 0 && code !== 200) return true
  }

  return false
}

/** 将接口字段安全转换为去除首尾空白的文本。 */
const toText = (value: unknown): string => {
  if (typeof value !== 'string' && typeof value !== 'number') return ''
  return String(value).trim()
}

const firstText = (record: UnknownRecord, keys: string[]): string => {
  for (const key of keys) {
    const value = toText(record[key])
    if (value) return value
  }

  return ''
}

const normalizeJumpType = (value: unknown): number | string | null => {
  return typeof value === 'number' || typeof value === 'string' ? value : null
}

/** 仅保留接口中的文本数组，避免异常字段进入模板渲染。 */
const normalizeTextList = (value: unknown): string[] | null => {
  if (!Array.isArray(value)) return null

  return value.map(toText).filter(Boolean)
}

/** 保留接口中的数字或数字文本，兼容数据中心不同版本的字段类型。 */
const normalizeNumberLike = (value: unknown): number | string | null => {
  return typeof value === 'number' || typeof value === 'string' ? value : null
}

/**
 * 按接口实际返回层级提取 Banner 数组。
 * SSR 通常保留最外层 data，客户端 request 拦截器可能已经剥掉该层，二者都需要兼容。
 */
export const extractStoreBannerItems = (response: unknown): UnknownRecord[] => {
  const search = (value: unknown, depth: number): UnknownRecord[] | null => {
    if (depth > 6) return null

    if (Array.isArray(value)) {
      return value.filter(isRecord)
    }

    if (!isRecord(value) || isBusinessFailure(value)) return null

    // 支持直接传入单条广告对象，便于兼容旧聚合层的返回形式。
    if (hasOwn(value, 'bannerImage') || hasOwn(value, 'image')) {
      return [value]
    }

    const advertisement = isRecord(value.advertisement) ? value.advertisement : null
    const bannerList = advertisement && isRecord(advertisement.apibanneradlist) ? advertisement.apibanneradlist : null
    if (bannerList && !isBusinessFailure(bannerList)) {
      if (Array.isArray(bannerList.data)) return bannerList.data.filter(isRecord)

      const nestedBannerData = search(bannerList.data, depth + 1)
      if (nestedBannerData) return nestedBannerData
    }

    const directBannerList = isRecord(value.apibanneradlist) ? value.apibanneradlist : null
    if (directBannerList && !isBusinessFailure(directBannerList)) {
      if (Array.isArray(directBannerList.data)) return directBannerList.data.filter(isRecord)

      const nestedDirectData = search(directBannerList.data, depth + 1)
      if (nestedDirectData) return nestedDirectData
    }

    if (Array.isArray(value.result)) return value.result.filter(isRecord)

    for (const nestedValue of [value.data, value.advertisement]) {
      const nestedItems = search(nestedValue, depth + 1)
      if (nestedItems) return nestedItems
    }

    return null
  }

  return search(response, 0) ?? []
}

/** 清洗单条 Banner，并要求至少存在可展示的图片地址。 */
export const normalizeStoreBannerItem = (value: unknown): StoreBannerItem | null => {
  if (!isRecord(value)) return null

  const bannerImage = firstText(value, ['bannerImage', 'image', 'imageUrl'])
  if (!bannerImage) return null

  return {
    id: firstText(value, ['id', 'code']) || undefined,
    bannerImage,
    marketId: normalizeNumberLike(value.marketId),
    webBannerPosition: normalizeNumberLike(value.webBannerPosition),
    appName: firstText(value, ['appName', 'name']),
    appTitle: firstText(value, ['appTitle', 'title']),
    traderName: firstText(value, ['traderName']),
    appLogo: firstText(value, ['appLogo', 'logo', 'icon']),
    appCoverBackground: firstText(value, ['appCoverBackground', 'coverBackground']),
    appBundleId: firstText(value, ['appBundleId']),
    traderCode: firstText(value, ['traderCode']),
    softwareId: firstText(value, ['softwareId']),
    screenshots: normalizeTextList(value.screenshots),
    downloadNum: normalizeNumberLike(value.downloadNum),
    sizes: firstText(value, ['sizes']),
    traderIco: firstText(value, ['traderIco', 'traderIcon']),
    jumpType: normalizeJumpType(value.jumpType),
    jumpTypeName: firstText(value, ['jumpTypeName']),
    code: firstText(value, ['code']),
    jumpLink: firstText(value, ['jumpLink', 'href', 'url']),
    language: firstText(value, ['language']),
    country: normalizeNumberLike(value.country),
    ad: firstText(value, ['ad']),
  }
}

/** 返回接口中所有可展示的 Banner，供首页不同位置按顺序消费。 */
export const normalizeStoreBannerItems = (response: unknown): StoreBannerItem[] => {
  return extractStoreBannerItems(response)
    .map((item) => normalizeStoreBannerItem(item))
    .filter((item): item is StoreBannerItem => item !== null)
}

/** 返回接口中的第一条有效 Banner；没有有效响应时交给组件使用本地默认值。 */
export const normalizeStoreBannerResponse = (response: unknown): StoreBannerItem | null => {
  return normalizeStoreBannerItems(response)[0] ?? null
}

/** 顶部 Banner 在接口 webBannerPosition 中的取值。 */
export const TOP_BANNER_POSITION = 1

/** 首页广告位在接口 webBannerPosition 中的取值。 */
export const AD_BANNER_POSITION = 2

/**
 * 按接口 webBannerPosition 把首页 Banner 拆成顶部轮播与广告位两组。
 * 保持接口返回顺序；位置缺失、为空或非 1/2 的条目直接丢弃，不做位置猜测。
 * @param items 归一化后的首页 Banner 列表
 * @returns 顶部 Banner 列表与广告位 Banner 列表
 */
export const splitStoreBannersByPosition = (
  items: readonly StoreBannerItem[],
): { topBanners: StoreBannerItem[]; adBanners: StoreBannerItem[] } => {
  const topBanners: StoreBannerItem[] = []
  const adBanners: StoreBannerItem[] = []

  for (const item of items) {
    const position = Number(item.webBannerPosition)

    if (position === TOP_BANNER_POSITION) topBanners.push(item)
    else if (position === AD_BANNER_POSITION) adBanners.push(item)
  }

  return { topBanners, adBanners }
}

/** Banner 点击目标解析所需的上下文。 */
export type StoreBannerLinkOptions = {
  /** 当前站点语言代码，用于给外部站点补齐语言段。 */
  languageCode: string
  /** 交易商编码到品牌别名的映射，用于跳转服务商详情页。 */
  traderAliases?: Readonly<TradingAppTraderAliasMap> | null
  /** 当前设备 User-Agent；排行榜需要按设备区分 iOS 与 Android 榜单。 */
  userAgent?: string
}

/**
 * 解析排行榜跳转使用的应用排行 slug。
 * 未显式传入 User-Agent 时读取浏览器环境，SSR 阶段读取不到则按默认平台处理。
 * @param userAgent 可选的 User-Agent
 * @returns `appdownload-ios` 或 `appdownload-android`
 */
export const getStoreBannerRankingSlug = (userAgent?: string): string => {
  const currentUserAgent = userAgent ?? (typeof navigator === 'undefined' ? '' : navigator.userAgent)

  return resolveTradingAppDefaultPlatform(currentUserAgent) === IOS_PLATFORM ? RANKING_SLUG_IOS : RANKING_SLUG_ANDROID
}

/** 仅允许显式 HTTP(S) 地址作为外链，避免把未知协议注入到链接属性。 */
export const resolveStoreBannerJumpLink = (value: unknown): string => {
  const text = typeof value === 'string' ? value.trim() : ''
  if (!text) return ''

  try {
    const url = new URL(text)
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.toString() : ''
  } catch {
    return ''
  }
}

/**
 * 解析单条 Banner 的点击目标。
 * 接口给出安全外链时优先按新窗口跳转该地址，否则按 jumpType 决定：
 * 交易应用跳软件详情页，服务商跳主站交易所详情页。
 * @param item 归一化后的 Banner 数据
 * @param options 语言代码与品牌别名映射
 * @returns 可外跳的完整地址；无法解析时返回空字符串，由组件保持不可点击
 */
export const resolveStoreBannerLink = (item: StoreBannerItem, options: StoreBannerLinkOptions): string => {
  const jumpLink = resolveStoreBannerJumpLink(item.jumpLink)
  if (jumpLink) return jumpLink

  const jumpType = Number(item.jumpType)

  if (jumpType === BANNER_JUMP_TYPE_SOFTWARE_APP) {
    return buildTradingAppDetailUrl(
      {
        marketId: item.marketId ?? undefined,
        appBundleId: item.appBundleId ?? undefined,
        traderCode: item.traderCode ?? undefined,
      },
      options.languageCode,
    )
  }

  if (jumpType === BANNER_JUMP_TYPE_TRADER) {
    const traderCode = item.traderCode?.trim() || item.code?.trim() || ''
    const alias = traderCode ? options.traderAliases?.[traderCode]?.trim() || '' : ''
    if (!alias) return ''

    return resolveExternalUrl(WIKIBIT_MAIN_ORIGIN, `/exchanges/${encodeURIComponent(alias)}`, options.languageCode)
  }

  if (jumpType === BANNER_JUMP_TYPE_RANKING) {
    return resolveExternalUrl(
      WIKIBIT_MAIN_ORIGIN,
      `/best-exchanges/${getStoreBannerRankingSlug(options.userAgent)}`,
      options.languageCode,
    )
  }

  return ''
}

/** 收集 Banner 中的交易商编码，供批量解析品牌别名使用。 */
export const collectStoreBannerTraderCodes = (items: readonly StoreBannerItem[]): string[] => {
  const traderCodes = new Set<string>()

  for (const item of items) {
    const traderCode = item.traderCode?.trim() || item.code?.trim() || ''
    if (traderCode) traderCodes.add(traderCode)
  }

  return [...traderCodes]
}
